import dayjs from 'dayjs';

import { AssignmentStatus } from '../../types/assignment-status';
import { UtilizationBasedOn } from '../../types/utilization-based-on';
import { PlannerPreferences } from '../../types/utilization-filters';
import { logPlannerNavigation } from '../helpers/log';
import { getBufferedVisibleViewportPeriod } from '../helpers/viewport';
import { PlannerSliceCreator, PreferencesSlice } from '../types';

/** Planner preferences (utilization settings) + zoom level. */
export const createPreferencesSlice: PlannerSliceCreator<PreferencesSlice> = set => ({
  plannerPreferences: {
    utilizationBasedOn: UtilizationBasedOn.TotalCapacity,
    assignmentStatuses: [
      AssignmentStatus.Confirmed,
      AssignmentStatus.Unconfirmed,
      AssignmentStatus.Draft,
    ],
    showFilteredOutAssignments: true,
    ignorePlaceholdersForGroup: false,
  },
  setPlannerPreferences: (plannerPreferences: PlannerPreferences) =>
    set(state => {
      // Preferences that are part of the query key (utilizationBasedOn,
      // assignmentStatuses, ignorePlaceholdersForGroup) create a NEW query
      // key when changed, so TanStack Query fires a fresh first fetch. That
      // fetch uses queryDateRanges[activeGroupBy] — which, after horizontal
      // scrolling, can be a wide expanded range (e.g. 2+ years), so the
      // refetch would load a huge window. Re-bootstrap the active group's
      // range to a small buffered window around the current viewport (same
      // as setFilters/resetFilter) so the preference-change fetch is scoped.
      //
      // showFilteredOutAssignments is UI-only (not in the query key) and
      // does not trigger a refetch, so narrowing the range for it is
      // harmless (no fetch happens).
      const firstVisibleDate = state.firstVisibleDate || dayjs();
      const activeGroupBy = state.groupBy;
      const bufferedPeriod = getBufferedVisibleViewportPeriod(
        state.dates,
        state.visibleDateRange,
        firstVisibleDate
      );

      logPlannerNavigation('setPlannerPreferences', {
        activeGroup: activeGroupBy,
        visibleBufferedRange: `${bufferedPeriod.startDate}..${bufferedPeriod.endDate}`,
      });

      return {
        plannerPreferences,
        queryDateRanges: {
          ...state.queryDateRanges,
          [activeGroupBy]: bufferedPeriod,
        },
      };
    }),

  zoomLevel: 1,
  // Round to 2 decimals so accumulated float steps (e.g. 1.7999999999999998)
  // don't leak into the persisted URL/localStorage value.
  setZoomLevel: (zoomLevel: number) => set({ zoomLevel: Math.round(zoomLevel * 100) / 100 }),
});
